import { Form, Formik, FormikHelpers, FormikProps } from "formik";
import moment from "moment";
import { useDispatch, useSelector } from "react-redux";
import {
  getPanVendorDataDefaultDateFilter,
  getPanVendorDataMinDate,
  PAN_VENDOR_DATA_DEFAULT_DAYS,
  setDateFilter,
  setFilterBy,
} from "src/redux/slices/PanVendorDataSlice";
import { AppDispatch, RootState } from "src/redux/store";
import { date, object } from "yup";
import PanVendorDataFilterCard from "./PanVendorDataFilterCard";

type Props = {
  close: () => void;
};

export type FormInitialValues = {
  status: string;
  applicationType: string;
  /** Acknowledgement-number prefix of the centre the form was logged at. */
  reqBranchCode: string;
  startDate: string | null;
  endDate: string | null;
};

/**
 * Filters for the PAN Vendor Data Report: the centre's Req. Branch Code,
 * application status, application type and the form's generated-date range. They travel to `/pan-form/generated-list`
 * and to the vendor XLSX through the slice's `filterBy` / `dateFilter`, so the
 * sheet always covers exactly the set on screen.
 */
const PanVendorDataFilterCardWrapper = ({ close }: Props) => {
  const dispatch = useDispatch<AppDispatch>();
  const panVendorDataState: any = useSelector(
    (state: RootState) => state.panVendorData
  );
  const { filterBy, dateFilter } = panVendorDataState;

  // Sub-admins (`ADMIN`) may only look back over the default window; SUPER_ADMIN
  // keeps the full range. Same check as the rest of the app (userData.type).
  const userData = JSON.parse(localStorage.getItem("userData") || "{}");
  const isSuperAdmin = userData?.type === "SUPER_ADMIN";
  const minDate = isSuperAdmin ? undefined : getPanVendorDataMinDate();

  const valueOf = (fieldName: string) =>
    filterBy?.find((filter: any) => filter.fieldName === fieldName)
      ?.value?.[0] || "";

  const initialValues: FormInitialValues = {
    status: valueOf("status"),
    applicationType: valueOf("applicationType"),
    reqBranchCode: valueOf("reqBranchCode"),
    startDate: dateFilter?.start_date || null,
    endDate: dateFilter?.end_date || null,
  };

  const minDateMessage = `Only the last ${PAN_VENDOR_DATA_DEFAULT_DAYS} days can be viewed`;

  const validationSchema = object({
    startDate: date()
      .nullable()
      .max(moment().endOf("day").toDate(), "Date should not be after current date")
      .min(
        minDate ? moment(minDate).startOf("day").toDate() : new Date(0),
        minDateMessage
      ),
    endDate: date()
      .nullable()
      .max(moment().endOf("day").toDate(), "Date should not be after current date")
      .min(
        minDate ? moment(minDate).startOf("day").toDate() : new Date(0),
        minDateMessage
      )
      .when("startDate", (startDate, schema) =>
        startDate ? schema.min(startDate, "Must be after start date") : schema
      ),
  });

  const handleSubmit = async (
    values: FormInitialValues,
    { setSubmitting }: FormikHelpers<FormInitialValues>
  ) => {
    setSubmitting(false);
    dispatch(
      setDateFilter({
        // A sub-admin who clears "Generated From" still only gets the window.
        start_date: values.startDate
          ? moment(values.startDate).format("yyyy-MM-DD")
          : minDate || "",
        end_date: values.endDate
          ? moment(values.endDate).format("yyyy-MM-DD")
          : "",
      })
    );
    await dispatch(
      setFilterBy(
        filterBy?.map((filter: any) => {
          switch (filter.fieldName) {
            case "status":
              return { ...filter, value: values.status ? [values.status] : [] };
            case "applicationType":
              return {
                ...filter,
                value: values.applicationType ? [values.applicationType] : [],
              };
            case "reqBranchCode":
              return {
                ...filter,
                value: values.reqBranchCode ? [values.reqBranchCode] : [],
              };
            default:
              return filter;
          }
        })
      )
    );
    close();
  };

  const handleReset = async (formikProps: FormikProps<FormInitialValues>) => {
    // Back to the default window the screen opens on, not "all time".
    await dispatch(setDateFilter(getPanVendorDataDefaultDateFilter()));
    await dispatch(
      setFilterBy(
        filterBy?.map((filter: any) =>
          filter.fieldName === "status" ||
          filter.fieldName === "applicationType" ||
          filter.fieldName === "reqBranchCode"
            ? { ...filter, value: [] }
            : filter
        )
      )
    );
    formikProps.resetForm();
  };

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      {(formikProps) => (
        <Form>
          <PanVendorDataFilterCard
            formikProps={formikProps}
            minDate={minDate}
            onReset={() => handleReset(formikProps)}
          />
        </Form>
      )}
    </Formik>
  );
};

export default PanVendorDataFilterCardWrapper;
